/**
 * Is this department's facility CMI still unset?
 *
 * CMI comes from one of two places: the CCN, which links the department to its CMS CASPER record,
 * or a hand-typed pair. With neither, the prediction is scored against the national-average pair
 * that ships as the seed — a number nobody at the facility chose, which quietly misstates every
 * HPPD measure on the tab. That is the state the setup flow exists to clear, so it is also the
 * condition that opens it.
 *
 * Deliberately answered against the PERSISTED config (`prediction.config`), never the live editing
 * draft: the flow's own first step writes a CCN into the draft, so reading the draft would close the
 * window the moment a facility is picked — before the turnover and hours steps, and before anything
 * is saved. The persisted row only moves when Finish calls through, which is exactly when the
 * window should go. The dialog's own "Next" gate is the one caller that passes the draft, because
 * there the question really is "has the user supplied a source yet?".
 *
 * Structurally typed so both the persisted `PbjModel` and the `PbjModelUpsertBody` draft satisfy it
 * (the draft's `cmiManual` is additionally nullable, meaning "leave the stored flag alone").
 */
export const needsPredictionSetup = (config: { ccn?: string | null; cmiManual?: boolean | null }): boolean => !config.ccn && !config.cmiManual;

/**
 * Whether the first-use flow belongs on screen. Takes the PREDICTION rather than a config, so the
 * call site cannot be handed the editing draft by mistake — the draft is what the flow's own first
 * step writes into, and reading it there dismissed the window on the first click. A draft has no
 * `config`, so that revert is now a type error rather than a bug to be caught in review.
 */
export const shouldOpenPredictionSetup = (prediction: { config: { ccn?: string | null; cmiManual?: boolean | null } }): boolean =>
	needsPredictionSetup(prediction.config);

/**
 * The draft after step 1's facility pick. Pure, because what it must NOT write is the load-bearing
 * part and that is far easier to pin here than through an autocomplete.
 *
 * `facilityId` is deliberately absent: it is the PBJ **submission** id (e.g. `"DE2880"`), a different
 * identifier in a different namespace from the CCN (e.g. `"065419"`). Seeding it from `provnum` would
 * file a configured department's export under the wrong id, and submission correctness is regulatory.
 * `stateCode` is filled only when the department has none — a department that named its own state is
 * not corrected from here.
 */
export const applyPickedFacility = <T extends { ccn?: string | null; stateCode: string | null }>(
	draft: T,
	picked: { provnum: string; state: string } | null,
): T => ({
	...draft,
	ccn: picked?.provnum ?? null,
	stateCode: draft.stateCode ? draft.stateCode : (picked?.state ?? null),
});

/** The setup flow's steps, in order. The dialog renders one per `activeStep`. */
export const SETUP_STEPS = ["facility", "turnover", "manualHours"] as const;

export type SetupStep = (typeof SETUP_STEPS)[number];

/** i18n key for each step's label in the stepper. */
export const SETUP_STEP_LABEL_KEY: Record<SetupStep, string> = {
	facility: "pbj_model.setup.step.facility.label",
	turnover: "pbj_model.setup.step.turnover.label",
	manualHours: "pbj_model.setup.step.manual_hours.label",
};
